import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Briefcase, DollarSign, Code, GraduationCap } from 'lucide-react';
import { seoPages } from '../data/seoPages';
import FooterTrust from '../components/FooterTrust';

const BOT_LINK = "https://t.me/instant_email_bot";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45 } } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const categoryIcons = {
  "Security / CCTV": <Shield className="w-5 h-5" />,
  "Hotel / Homestay": <Briefcase className="w-5 h-5" />,
  "Business / Sales": <Zap className="w-5 h-5" />,
  "Finance": <DollarSign className="w-5 h-5" />,
  "IT / Tech": <Code className="w-5 h-5" />,
  "Education / Others": <GraduationCap className="w-5 h-5" />,
};

export default function UseCasesIndex() {
  const categories = [...new Set(seoPages.map(p => p.category))];

  React.useEffect(() => {
    document.title = "Use Cases – TelegramEmailBot | Email to Telegram for Every Industry";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Discover 30+ ways to use TelegramEmailBot. CCTV alerts, hotel bookings, lead capture, invoices, server monitoring, and more — all forwarded to Telegram instantly.');
    const canonicalHref = 'https://telegramemailbot.com/use-cases';
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.setAttribute('href', canonicalHref);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalHref);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200/60 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TelegramEmailBot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-gray-900">TelegramEmailBot</span>
          </Link>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all shadow-md">
            Get Started ($4.99)
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20 pb-16 sm:pt-28 sm:pb-20 px-4 text-center" data-testid="use-cases-index-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900 mb-5 leading-[1.1]">
              Turn Important Emails Into Instant Telegram Alerts
            </motion.h1>
            <motion.p variants={fadeUp} className="font-manrope text-base sm:text-lg text-gray-500 max-w-2xl mx-auto mb-8 leading-relaxed">
              Never miss bookings, CCTV alerts, invoices, leads, or system warnings again. 30+ use cases for every industry.
            </motion.p>
            <motion.a variants={fadeUp} href={BOT_LINK} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0088CC] text-white font-manrope font-bold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg"
            >
              Get Started ($4.99) <ArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((cat, ci) => (
        <section key={cat} className={`py-14 sm:py-18 px-4 ${ci % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-white'}`} data-testid={`use-cases-cat-${ci}`}>
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#0088CC]/10 rounded-xl flex items-center justify-center text-[#0088CC]">
                  {categoryIcons[cat] || <Zap className="w-5 h-5" />}
                </div>
                <h2 className="font-outfit font-bold text-2xl text-gray-900">{cat}</h2>
              </motion.div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {seoPages.filter(p => p.category === cat).map((page, i) => (
                  <motion.div key={page.slug} variants={fadeUp}>
                    <Link to={`/use-cases/${page.slug}`} className="block bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md hover:border-gray-300 transition-all group h-full" data-testid={`use-case-link-${page.slug}`}>
                      <h3 className="font-outfit font-bold text-base text-gray-900 group-hover:text-[#0088CC] transition-colors mb-2">{page.headline}</h3>
                      <p className="font-manrope text-sm text-gray-500 leading-relaxed line-clamp-2 mb-3">{page.subheadline}</p>
                      <span className="font-manrope text-xs font-semibold text-[#0088CC] flex items-center gap-1">
                        Learn more <ArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 px-4 bg-gray-900 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-outfit font-bold text-3xl text-white mb-3">Ready to Get Started?</h2>
          <p className="font-manrope text-gray-400 mb-2">Free 1 month trial. Then just</p>
          <p className="font-outfit font-black text-5xl text-white mb-6">$14.99<span className="text-xl text-gray-500 font-normal">/year</span></p>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0088CC] text-white font-manrope font-bold px-10 py-4 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg">
            Get Started for $4.99 <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-gray-300 py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TelegramEmailBot" className="w-5 h-5 rounded object-cover" />
              <span className="font-outfit font-bold text-white text-sm">TelegramEmailBot</span>
            </div>
            <div className="flex items-center gap-6 font-manrope text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <span className="font-manrope text-xs text-gray-300">&copy; 2026 TelegramEmailBot</span>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>
    </div>
  );
}
